import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, errorText } from "@/lib/api";
import "./StatLeaders.css";

const COLS = [
  { key: "min", label: "MIN" }, { key: "pts", label: "PTS" },
  { key: "fgm", label: "FGM" }, { key: "fga", label: "FGA" }, { key: "fg_pct", label: "FG%" },
  { key: "fg3m", label: "3PM" }, { key: "fg3a", label: "3PA" }, { key: "fg3_pct", label: "3P%" },
  { key: "ftm", label: "FTM" }, { key: "fta", label: "FTA" }, { key: "ft_pct", label: "FT%" },
  { key: "reb", label: "REB" }, { key: "ast", label: "AST" },
  { key: "stl", label: "STL" }, { key: "blk", label: "BLK" }, { key: "tov", label: "TO" },
];
const VALID = new Set(COLS.map((c) => c.key));

export default function StatsTable() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initial = params.get("sort");
  const [sort, setSort] = useState(VALID.has(initial) ? initial : "pts");

  useEffect(() => {
    let alive = true;
    api.get("/player-stats?limit=300")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(errorText(e)));
    return () => { alive = false; };
  }, []);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows].sort((a, b) => (b[sort] ?? -1) - (a[sort] ?? -1));
  }, [data, sort]);

  if (error) return <div className="sl-wrap"><div className="sl-state">{error}</div></div>;
  if (!data) return <div className="sl-wrap"><div className="sl-state">Loading player stats…</div></div>;

  return (
    <div className="sl-wrap sl-wide" data-testid="stats-table-page">
      <div className="sl-top">
        <div>
          <span className="sl-overline">League Leaders</span>
          <h1 className="sl-title">NBA Player Stats <em>{data.season_label}</em></h1>
        </div>
        <button type="button" className="sl-back" onClick={() => navigate("/stats")} data-testid="stats-table-back">← Leaders</button>
      </div>
      <div className="st-scroll">
        <table className="st-table" data-testid="stats-table">
          <thead>
            <tr>
              <th className="st-rk">RK</th>
              <th className="st-name">Name</th>
              <th>GP</th>
              {COLS.map((c) => (
                <th key={c.key} className={`st-sortable ${sort === c.key ? "is-sort" : ""}`} onClick={() => setSort(c.key)} data-testid={`st-head-${c.key}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.player_id} className="st-clickable" role="button" tabIndex={0}
                onClick={() => navigate(`/player/${r.player_id}`)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/player/${r.player_id}`)}
                data-testid={`st-row-${i}`}>
                <td className="st-rk">{i + 1}</td>
                <td className="st-name"><b>{r.name}</b><small>{r.team}{r.position ? ` · ${r.position}` : ""}</small></td>
                <td>{r.gp}</td>
                {COLS.map((c) => (
                  <td key={c.key} className={sort === c.key ? "is-sort" : ""}>{r[c.key] == null ? "—" : r[c.key]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="sl-note">Season per-game averages · percentages from makes/attempts · tap a column to sort</p>
    </div>
  );
}
